// serviceSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IService, IServiceInitialState } from "./serviceModal";
import {
  activeInactiveService,
  createService,
  deleteService,
  getAllActiveServices,
  getAllServices,
  getServiceByID,
  serviceImageUpload,
  updateService,
} from "./services/service.services";

const initialState: IServiceInitialState = {
  loading: false,
  formLoading: false,
  error: "",
  ServiceID: null,
  ServiceList: [],
  ServiceDetail: {
    ID: null,
    Title: "",
    URL: "",
    ShortDescription: "",
    LongDescription: "",
    KeyFeature: "",
    SequenceNo: null,
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
    IconImage: "",
    MainImage: "",
    BannerImage: "",
    IsActive: 1,
  },
};

const serviceSlice = createSlice({
  name: "service",
  initialState,
  reducers: {
    clearServiceDetails: (state) => {
      state.ServiceDetail = initialState.ServiceDetail;
    },
    setServiceID: (state, action) => {
      state.ServiceID = action.payload;
    },
    clearServiceID: (state) => {
      state.ServiceID = initialState.ServiceID;
    },
  },
  extraReducers: (builder) => {
    // GET ALL
    builder.addCase(getAllServices.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllServices.fulfilled,
      (state, action: PayloadAction<IService[]>) => {
        state.loading = false;
        state.ServiceList = action.payload || [];
      },
    );
    builder.addCase(getAllServices.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ServiceList = [];
    });

    // CREATE
    builder.addCase(createService.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createService.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createService.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // UPDATE
    builder.addCase(updateService.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateService.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateService.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // DELETE
    builder.addCase(deleteService.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteService.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteService.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // GET BY ID
    builder.addCase(getServiceByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(
      getServiceByID.fulfilled,
      (state, action: PayloadAction<IService>) => {
        state.formLoading = false;
        state.ServiceDetail = action.payload;
      },
    );
    builder.addCase(getServiceByID.rejected, (state, action) => {
      state.formLoading = false;
      state.ServiceDetail = initialState.ServiceDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // GET ALL ACTIVE
    builder.addCase(getAllActiveServices.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveServices.fulfilled,
      (state, action: PayloadAction<IService[]>) => {
        state.loading = false;
        state.ServiceList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveServices.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ServiceList = [];
    });

    // ACTIVE / INACTIVE
    builder.addCase(activeInactiveService.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(activeInactiveService.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveService.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // IMAGE UPLOAD
    builder.addCase(serviceImageUpload.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(serviceImageUpload.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(serviceImageUpload.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const { clearServiceDetails, setServiceID, clearServiceID } =
  serviceSlice.actions;

export default serviceSlice.reducer;
